import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Driver } from "../../../../../services";
import SwitchTabs from "../../../../shared/SwitchTabs/SwitchTabs";
import PlusIcon from "../../../../shared/icons/PlusIcon";
import style from "./AssignDriverModal.module.scss";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import SearchIcon from "../../../../shared/icons/SearchIcon";
import CheckIcon from "../../../../shared/icons/CheckIcon";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CustomTextarea from "../../../../custom/custom-textarea/CustomTextarea";
import CustomDatePicker from "../../../../shared/date-picker/Date/CustomDatePicker";
import CalendarIcon from "../../../../shared/icons/CalendarIcon";

const EMPTY_VALUE = "—";

const getInitials = (surname, name) => `${(surname || "").charAt(0)}${(name || "").charAt(0)}`.toUpperCase() || EMPTY_VALUE;

const AssignDriverModal = ({
  isOpen,
  onClose,
  order,
  mode = "add",
  onSuccess,
  driverRecordId = null,
  currentDriverId = null,
  currentVehicleId = null,
  initialTab = "driver",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const company = useSelector((s) => s.user.company);

  const tabs = useMemo(
    () => [
      { key: "driver", text: t("orderInfoExpeditor.assignDriverModal.driverTab") },
      { key: "vehicle", text: t("orderInfoExpeditor.assignDriverModal.vehicleTab") },
    ],
    [t],
  );

  const [activeTab, setActiveTab] = useState("driver");
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [driverSearch, setDriverSearch] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [routeDescription, setRouteDescription] = useState("");
  const [cargoTransferDate, setCargoTransferDate] = useState(null);

  const isSecondDriver = (order?.drivers?.length || 0) >= 1;
  const showExtraFields = isSecondDriver && mode !== "replace";

  useEffect(() => {
    if (!isOpen || !company?.id) return;
    Driver.getDriversListByExpeditor(company.id)
      .then((r) => {
        if (r.status === 200) setDrivers(r.data || []);
      })
      .catch(() => {});
    Driver.getVehicleByExpeditor(company.id)
      .then((r) => {
        if (r.status === 200) setVehicles(r.data || []);
      })
      .catch(() => {});
  }, [isOpen, company?.id]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setSelectedDriverId(currentDriverId);
      setSelectedVehicleId(currentVehicleId);
      setDriverSearch("");
      setVehicleSearch("");
      setRouteDescription("");
      setCargoTransferDate(null);
    }
  }, [isOpen]);

  const filteredDrivers = useMemo(() => {
    const result = drivers.filter((d) => {
      const name = [d.surname, d.name, d.patronymic].filter(Boolean).join(" ").toLowerCase();
      return name.includes(driverSearch.toLowerCase());
    });
    return [...result].sort((a, b) => {
      if (a.id === currentDriverId) return -1;
      if (b.id === currentDriverId) return 1;
      return b.isFree - a.isFree;
    });
  }, [drivers, driverSearch, currentDriverId]);

  const filteredVehicles = useMemo(() => {
    const result = vehicles.filter((v) =>
      `${v.carModel || ""} ${v.carNumber || ""}`.toLowerCase().includes(vehicleSearch.toLowerCase()),
    );
    return [...result].sort((a, b) => {
      if (a.id === currentVehicleId) return -1;
      if (b.id === currentVehicleId) return 1;
      return b.isFree - a.isFree;
    });
  }, [vehicles, vehicleSearch, currentVehicleId]);

  const handleDriverClick = (driver) => {
    if (!driver.isFree && driver.id !== currentDriverId) {
      toast.info(t("orderInfoExpeditor.assignDriverModal.driverBusy"));
      return;
    }
    const newId = selectedDriverId === driver.id ? null : driver.id;
    setSelectedDriverId(newId);
  };

  const handleVehicleClick = (vehicle) => {
    if (!vehicle.isFree && vehicle.id !== currentVehicleId) {
      toast.info(t("orderInfoExpeditor.assignDriverModal.vehicleBusy"));
      return;
    }
    const newId = selectedVehicleId === vehicle.id ? null : vehicle.id;
    setSelectedVehicleId(newId);
  };

  const handleSubmit = async () => {
    if (!selectedDriverId || !selectedVehicleId) {
      toast.warn(t("orderInfoExpeditor.assignDriverModal.selectDriverAndVehicle"));
      return;
    }
    setIsSubmitting(true);
    try {
      const data = {
        driverId: selectedDriverId,
        vehicleId: selectedVehicleId,
        ...(showExtraFields && {
          comment: routeDescription.trim(),
          shipmentTransferDate: cargoTransferDate ? cargoTransferDate.toISOString() : null,
        }),
      };
      if (mode === "replace") {
        await Driver.updateAssignDriverByExpeditor(order.id, driverRecordId, data);
      } else {
        await Driver.assignDriverByExpeditor(order.id, data);
      }
      toast.success(
        mode === "replace"
          ? t("orderInfoExpeditor.assignDriverModal.driverReplaced")
          : t("orderInfoExpeditor.assignDriverModal.driverAssigned"),
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch {
      toast.error(t("common.genericError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedDriver = drivers.find((d) => d.id === selectedDriverId);
  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={onClose} data-portal-modal="true">
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <div className={style.topbar}>
          <span className={style.title}>
            {mode === "replace"
              ? t("orderInfoExpeditor.assignDriverModal.replaceTitle")
              : t("orderInfoExpeditor.assignDriverModal.addTitle")}
          </span>
          <button
            className={style.close_btn}
            onClick={onClose}
            aria-label={t("orderInfoExpeditor.assignDriverModal.close")}
          >
            <CloseIconNew width="24" height="24" fill="#212121" />
          </button>
        </div>
          <SwitchTabs options={tabs} activeOption={activeTab} onOptionChange={setActiveTab} />

        <div className={style.content}>

          {activeTab === "driver" && (
            <div className={style.section}>
              <div className={style.section_header}>
                <span className={style.section_title}>{t("orderInfoExpeditor.assignDriverModal.selectDriver")}</span>
                <button className={style.add_link} onClick={() => navigate("/drivers/create")}>
                  <PlusIcon width="20" height="20" fill="#0d3b66" />
                  <span>{t("orderInfoExpeditor.assignDriverModal.addDriver")}</span>
                </button>
              </div>
              <div className={style.search_wrap}>
                <SearchIcon />
                <input
                  className={style.search_input}
                  placeholder={t("orderInfoExpeditor.assignDriverModal.driverSearchPlaceholder")}
                  value={driverSearch}
                  onChange={(e) => setDriverSearch(e.target.value)}
                />
              </div>
              <div className={style.list}>
                {filteredDrivers.length === 0 ? (
                  <div className={style.list_empty}>{t("orderInfoExpeditor.assignDriverModal.driversNotFound")}</div>
                ) : (
                  filteredDrivers.map((driver) => {
                    const fullName = [driver.surname, driver.name].filter(Boolean).join(" ");
                    const selected = selectedDriverId === driver.id;
                    const isCurrent = driver.id === currentDriverId;
                    return (
                      <div
                        key={driver.id}
                        className={`${style.list_item} ${!driver.isFree && !isCurrent ? style.list_item_disabled : ""}`}
                        onClick={() => handleDriverClick(driver)}
                      >
                        <div className={`${style.checkbox} ${selected ? style.checkbox_checked : ""}`}>
                          {selected && <CheckIcon />}
                        </div>
                        <div className={style.avatar}>{getInitials(driver.surname, driver.name)}</div>
                        <div className={style.item_info}>
                          <span className={style.item_name}>{fullName || EMPTY_VALUE}</span>
                          <span className={style.item_sub}>{driver.phoneNumber ? `+${driver.phoneNumber}` : EMPTY_VALUE}</span>
                        </div>
                        {isCurrent ? (
                          <div className={`${style.status_badge} ${style.badge_current}`}>
                            {t("orderInfoExpeditor.assignDriverModal.currentDriver")}
                          </div>
                        ) : (
                          <div className={`${style.status_badge} ${driver.isFree ? style.badge_free : style.badge_busy}`}>
                            {driver.isFree
                              ? t("orderInfoExpeditor.assignDriverModal.free")
                              : t("orderInfoExpeditor.assignDriverModal.busy")}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {activeTab === "vehicle" && (
            <div className={style.section}>
              <div className={style.section_header}>
                <span className={style.section_title}>{t("orderInfoExpeditor.assignDriverModal.selectVehicle")}</span>
                <button className={style.add_link} onClick={() => navigate("/cars/create")}>
                  <PlusIcon width="20" height="20" fill="#0d3b66" />
                  <span>{t("orderInfoExpeditor.assignDriverModal.addVehicle")}</span>
                </button>
              </div>
              <div className={style.search_wrap}>
                <SearchIcon />
                <input
                  className={style.search_input}
                  placeholder={t("orderInfoExpeditor.assignDriverModal.vehicleSearchPlaceholder")}
                  value={vehicleSearch}
                  onChange={(e) => setVehicleSearch(e.target.value)}
                />
              </div>
              <div className={style.list}>
                {filteredVehicles.length === 0 ? (
                  <div className={style.list_empty}>{t("orderInfoExpeditor.assignDriverModal.vehiclesNotFound")}</div>
                ) : (
                  filteredVehicles.map((vehicle) => {
                    const selected = selectedVehicleId === vehicle.id;
                    const isCurrent = vehicle.id === currentVehicleId;
                    const initials = (vehicle.carNumber || t("orderInfoExpeditor.assignDriverModal.vehicleTab")).slice(0, 2).toUpperCase();
                    return (
                      <div
                        key={vehicle.id}
                        className={`${style.list_item} ${!vehicle.isFree && !isCurrent ? style.list_item_disabled : ""}`}
                        onClick={() => handleVehicleClick(vehicle)}
                      >
                        <div className={`${style.checkbox} ${selected ? style.checkbox_checked : ""}`}>
                          {selected && <CheckIcon />}
                        </div>
                        <div className={`${style.avatar} ${style.avatar_vehicle}`}>{initials}</div>
                        <div className={style.item_info}>
                          <span className={style.item_name}>{vehicle.carModel || EMPTY_VALUE}</span>
                          <span className={style.item_sub}>
                            {vehicle.carNumber || EMPTY_VALUE}
                            {vehicle.vehicleTypeName ? ` · ${vehicle.vehicleTypeName}` : ""}
                          </span>
                        </div>
                        {isCurrent ? (
                          <div className={`${style.status_badge} ${style.badge_current}`}>
                            {t("orderInfoExpeditor.assignDriverModal.currentVehicle")}
                          </div>
                        ) : (
                          <div className={`${style.status_badge} ${vehicle.isFree ? style.badge_free : style.badge_busy}`}>
                            {vehicle.isFree
                              ? t("orderInfoExpeditor.assignDriverModal.free")
                              : t("orderInfoExpeditor.assignDriverModal.busy")}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {(selectedDriver || selectedVehicle) && (
            <div className={style.summary_bar}>
              {selectedDriver && (
                <div className={style.summary_col}>
                  <span className={style.summary_label}>{t("orderInfoExpeditor.assignDriverModal.driverTab")}</span>
                  <div className={style.summary_row}>
                    <div className={style.avatar}>
                      {getInitials(selectedDriver.surname, selectedDriver.name)}
                    </div>
                    <div className={style.summary_info}>
                      <span className={style.summary_name}>
                        {[selectedDriver.surname, selectedDriver.name].filter(Boolean).join(" ") || EMPTY_VALUE}
                      </span>
                      <span className={style.summary_sub}>
                        {selectedDriver.phoneNumber ? `+${selectedDriver.phoneNumber}` : EMPTY_VALUE}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {selectedVehicle && (
                <div className={style.summary_col}>
                  <span className={style.summary_label}>{t("orderInfoExpeditor.assignDriverModal.vehicleTab")}</span>
                  <div className={style.summary_row}>
                    <div className={`${style.avatar} ${style.avatar_vehicle}`}>
                      {(selectedVehicle.carNumber || "").slice(0, 2).toUpperCase() || EMPTY_VALUE}
                    </div>
                    <div className={style.summary_info}>
                      <span className={style.summary_name}>{selectedVehicle.carModel || EMPTY_VALUE}</span>
                      <span className={style.summary_sub}>{selectedVehicle.vehicleTypeName || EMPTY_VALUE}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {showExtraFields && (
            <div className={style.extra_fields}>
              <CustomTextarea
                value={routeDescription}
                isBgGray
                rows={2}
                labelText={t("orderInfoExpeditor.assignDriverModal.routeDescriptionLabel")}
                placeholder={t("orderInfoExpeditor.assignDriverModal.routeDescriptionPlaceholder")}
                onChangeValue={setRouteDescription}
              />
              <div className={style.date_field}>
                <span className={style.date_label}>
                  {t("orderInfoExpeditor.assignDriverModal.cargoTransferDateLabel")}
                </span>
                <CustomDatePicker
                  selected={cargoTransferDate}
                  placeholderText={t("orderFilter.datePlaceholder")}
                  dateFormat="dd.MM.yyyy"
                  onChange={setCargoTransferDate}
                  popperProps={{ strategy: "fixed" }}
                />
                <CalendarIcon />
              </div>
            </div>
          )}
        </div>

        <CustomButton
          text={t("orderInfo.assignDriver.assignButton")}
          isNavy
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          isFull
        />
      </div>
    </div>,
    document.body
  );
};

export default AssignDriverModal;
